// Browser-side video transcode, used before chat media goes to S3.
//
// Two paths, in order of preference:
//
// 1. WebCodecs (mediabunny) - demux, decode, re-encode and mux with the browser's
//    own codecs. Not tied to playback speed, so this is the path that keeps the wait
//    short. Measured 3.4x real time for a 1080p source down to 720p on an Intel iGPU
//    laptop; a 20s clip takes about 6s. mediabunny is dynamically imported so it
//    lands in its own ~174 KB gzipped chunk, downloaded only by users who actually
//    attach a video.
//
//    Encoding is the bottleneck (demux measured ~2800x, decode ~49x, encode ~2x), so
//    the throughput ceiling is whatever H.264 encoder the browser gives us. Output
//    resolution is the only lever that moves it, and 1280 is the sweet spot: 1080p
//    output drops to 1.2x, while 854 and 640 are no faster than 1280 because below
//    that the fixed per-clip cost dominates rather than pixel count.
//
// 2. MediaRecorder capture - plays the clip into a canvas and records the stream.
//    Locked to 1x real time by construction (the <video> element decodes at playback
//    speed and nothing changes that), so it is only the fallback for browsers without
//    WebCodecs.
//
// ffmpeg.wasm is deliberately not used: the threaded build needs SharedArrayBuffer,
// which needs COOP/COEP on this origin, and Cross-Origin-Embedder-Policy: require-corp
// would break the CloudFront media and the Stripe payment iframe. The single-threaded
// build is slower than option 2.

import {
  AUDIO_BITS_PER_SECOND,
  MAX_DURATION_SECONDS,
  OUTPUT_FPS,
  clamp,
  planVideoEncode,
  retryBitrate,
  transcodeVideo,
} from './video-transcode';

const STALL_TIMEOUT_MS = 20 * 1000;
const OPTIONAL_DEADLINE_MIN_MS = 30 * 1000;
const OPTIONAL_DEADLINE_PER_SECOND_MS = 1500;
const PLAYBACK_GRACE_MS = 10 * 1000;

const RETRY_WITH_RECORDER = new Set(['undecodable', 'no-encoder', 'unsupported', 'failed']);

// Confirms an encoded blob actually carries a video track. A stalled capture
// yields a perfectly valid audio-only file, and uploading that silently is worse
// than not compressing at all - so nothing is returned without this passing.
function hasVideoTrack(blob) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const probe = document.createElement('video');
    const finish = (result) => {
      URL.revokeObjectURL(url);
      resolve(result);
    };

    probe.preload = 'metadata';
    probe.onloadedmetadata = () => finish(probe.videoWidth > 0 && probe.videoHeight > 0);
    probe.onerror = () => finish(false);
    probe.src = url;
  });
}

function toFile(blob, sourceName, mimeType) {
  const baseName = sourceName.replace(/\.[^.]+$/, '') || 'video';
  const extension = mimeType.startsWith('video/mp4') ? 'mp4' : 'webm';

  return new File([blob], `${baseName}.${extension}`, {
    type: mimeType,
    lastModified: Date.now(),
  });
}

function hasWebCodecs() {
  return (
    typeof VideoEncoder !== 'undefined' &&
    typeof VideoDecoder !== 'undefined' &&
    typeof VideoFrame !== 'undefined'
  );
}

function hasMediaRecorderCapture() {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof MediaRecorder.isTypeSupported === 'function' &&
    typeof HTMLCanvasElement !== 'undefined' &&
    typeof HTMLCanvasElement.prototype.captureStream === 'function' &&
    pickRecorderType() !== null
  );
}

export function canCompressVideo() {
  return typeof document !== 'undefined' && (hasWebCodecs() || hasMediaRecorderCapture());
}

/**
 * Re-encodes `file` to fit `targetBytes`, downscaled to `maxDimension` on the long
 * edge. Returns a File, or null when compression is not possible or not worth it
 * (undecodable source, too long, result no smaller than the original, or an output
 * that somehow carries no video) - callers fall back to the original file.
 *
 * `onProgress` is called with 0..1. On the WebCodecs path this typically completes
 * in a fraction of the clip's duration; on the MediaRecorder fallback it takes at
 * least as long as the clip itself, so callers must show progress either way.
 */
export async function compressVideo(file, options = {}) {
  if (!file) return null;

  const mustShrink = file.size > options.targetBytes;

  if (hasWebCodecs()) {
    const outcome = await transcodeGuarded(file, options, mustShrink);
    if (outcome?.blob) return toFile(outcome.blob, file.name, outcome.mimeType);
    if (!mustShrink || !RETRY_WITH_RECORDER.has(outcome?.reason)) return null;
  }

  if (!mustShrink || !hasMediaRecorderCapture()) return null;

  try {
    return await recordInRealTime(file, options);
  } catch {
    return null;
  }
}

function startInWorker(file, { targetBytes, maxDimension }, handlers) {
  let worker;
  return import('./video-compression-worker-factory').then(({ createVideoCompressionWorker }) => {
    worker = createVideoCompressionWorker();

    const done = new Promise((resolve, reject) => {
      worker.onmessage = ({ data }) => {
        if (data?.type === 'progress') handlers.onProgress(data.value);
        else if (data?.type === 'plan') handlers.onPlan(data);
        else if (data?.type === 'done') resolve(data.outcome || { reason: 'failed' });
        else if (data?.type === 'error') resolve({ reason: 'failed', message: data.message });
      };
      worker.onerror = (event) => {
        event.preventDefault?.();
        reject(new Error('Video worker could not start'));
      };
    });

    worker.postMessage({ type: 'compress', file, targetBytes, maxDimension });

    return { done, abort: () => worker.terminate() };
  });
}

function startOnMainThread(file, { targetBytes, maxDimension }, handlers) {
  let cancel = null;
  const done = transcodeVideo(file, {
    targetBytes,
    maxDimension,
    onProgress: handlers.onProgress,
    onPlan: handlers.onPlan,
    onCancelable: (fn) => {
      cancel = fn;
    },
  }).catch((error) => ({ reason: 'failed', message: String(error?.message || error) }));

  return { done, abort: () => cancel?.() };
}

function transcodeGuarded(file, options, mustShrink) {
  return new Promise((resolve) => {
    let settled = false;
    let lastBeat = Date.now();
    let deadlineAt = Infinity;
    let job = null;

    const finish = (outcome) => {
      if (settled) return;
      settled = true;
      clearInterval(watchdog);
      job?.abort();
      resolve(outcome);
    };

    const handlers = {
      onProgress: (value) => {
        lastBeat = Date.now();
        options.onProgress?.(clamp(value, 0, 1));
      },
      onPlan: ({ duration }) => {
        lastBeat = Date.now();
        if (!mustShrink) {
          deadlineAt = Date.now() + Math.max(OPTIONAL_DEADLINE_MIN_MS, duration * OPTIONAL_DEADLINE_PER_SECOND_MS);
        }
      },
    };

    const watchdog = setInterval(() => {
      const now = Date.now();
      if (now - lastBeat > STALL_TIMEOUT_MS || now > deadlineAt) finish({ reason: 'timeout' });
    }, 1000);

    const run = (starter) =>
      Promise.resolve(starter(file, options, handlers)).then((started) => {
        job = started;
        if (settled) {
          started.abort();
          return null;
        }
        return started.done;
      });

    run(startInWorker)
      .catch(() => {
        job?.abort();
        lastBeat = Date.now();
        return run(startOnMainThread);
      })
      .then((outcome) => finish(outcome || { reason: 'failed' }))
      .catch(() => finish({ reason: 'failed' }));
  });
}

// ---------------------------------------------------------------------------
// Fallback: real-time MediaRecorder capture
// ---------------------------------------------------------------------------

// Preferred first: mp4 plays everywhere including Safari. webm is the fallback
// for browsers whose MediaRecorder cannot produce mp4 (Firefox, older Chrome).
const RECORDER_TYPES = [
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

function pickRecorderType() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return null;
  return RECORDER_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) ?? null;
}

function loadVideoElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');

    video.preload = 'auto';
    video.playsInline = true;

    const fail = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Video could not be decoded'));
    };

    video.onerror = fail;
    // loadeddata rather than loadedmetadata: the first frame has to be decoded
    // before the draw loop starts, or the clip opens on a blank canvas.
    video.onloadeddata = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) {
        fail();
        return;
      }
      resolve({ video, url });
    };

    video.src = url;
  });
}

// Route audio through Web Audio rather than letting captureStream() take it off
// the element: the element has to stay silent while it plays (nobody wants the
// clip blaring during upload), and muting it would also mute what captureStream
// hands the recorder. A MediaStreamDestination that is never connected to
// audioContext.destination gives a live track and no sound.
function attachAudioTrack(video, stream) {
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextCtor) return null;

  const audioContext = new AudioContextCtor();
  const sourceNode = audioContext.createMediaElementSource(video);
  const destinationNode = audioContext.createMediaStreamDestination();

  sourceNode.connect(destinationNode);
  destinationNode.stream.getAudioTracks().forEach((track) => stream.addTrack(track));

  return audioContext;
}

// Draws are driven by a plain timer.
//
// requestVideoFrameCallback looks like the right tool and is not: it fires once
// per *presented* frame, so a video element the compositor never paints - offscreen,
// backgrounded, or simply not in the document, which is exactly our case - never
// fires it at all. That produced recordings with an audio track and no video.
// drawImage() reads whatever frame is currently decoded regardless of presentation,
// so a timer at the output rate is both simpler and the only reliable option.
function startFrameLoop(draw) {
  const timer = setInterval(draw, 1000 / OUTPUT_FPS);
  return () => clearInterval(timer);
}

function rewind(video) {
  return new Promise((resolve) => {
    if (!video.currentTime) {
      resolve();
      return;
    }
    const done = () => {
      video.onseeked = null;
      resolve();
    };
    video.onseeked = done;
    video.currentTime = 0;
    // Some containers never fire seeked after playback ended.
    setTimeout(done, 1000);
  });
}

async function recordInRealTime(file, { targetBytes, maxDimension, onProgress }) {
  const mimeType = pickRecorderType();
  let video = null;
  let objectUrl = null;
  let audioContext = null;
  let stream = null;

  try {
    ({ video, url: objectUrl } = await loadVideoElement(file));

    const duration = video.duration;
    if (duration > MAX_DURATION_SECONDS) return null;

    const plan = planVideoEncode({
      fileSize: file.size,
      duration,
      sourceWidth: video.videoWidth,
      sourceHeight: video.videoHeight,
      maxDimension,
      targetBytes,
    });
    if (!plan || !plan.worthwhile) return null;

    const { size } = plan;
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const context = canvas.getContext('2d');

    stream = canvas.captureStream(OUTPUT_FPS);

    // No audio route means we would ship a silently muted clip, which is worse
    // than shipping the original untouched. createMediaElementSource can only be
    // called once per element, so the graph is built here and reused per pass.
    audioContext = attachAudioTrack(video, stream);
    if (!audioContext) return null;

    const encodePass = async (videoBitsPerSecond) => {
      await rewind(video);

      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: Math.floor(videoBitsPerSecond),
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
      });

      const chunks = [];
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunks.push(event.data);
      };

      const recorded = new Promise((resolve, reject) => {
        recorder.onstop = resolve;
        recorder.onerror = reject;
      });

      let playbackTimer = null;
      const played = new Promise((resolve, reject) => {
        video.onended = resolve;
        video.onerror = () => reject(new Error('Playback failed during compression'));
        playbackTimer = setTimeout(
          () => reject(new Error('Playback stalled during compression')),
          duration * 1000 + PLAYBACK_GRACE_MS
        );
      });

      const stopFrameLoop = startFrameLoop(() => {
        context.drawImage(video, 0, 0, size.width, size.height);
      });

      if (onProgress) {
        video.ontimeupdate = () => onProgress(clamp(video.currentTime / duration, 0, 1));
      }

      try {
        recorder.start(1000);
        await video.play();
        await played;
        // The last frames are still in flight when 'ended' fires.
        await new Promise((resolve) => setTimeout(resolve, 200));
        recorder.stop();
        await recorded;
      } finally {
        clearTimeout(playbackTimer);
        stopFrameLoop();
        video.ontimeupdate = null;
        video.onended = null;
        if (recorder.state !== 'inactive') recorder.stop();
      }

      return new Blob(chunks, { type: mimeType });
    };

    const bitrate = plan.videoBitsPerSecond;
    let blob = await encodePass(bitrate);

    // Single-pass VBR overshoots badly on noisy footage, and an over-budget result
    // is rejected downstream after the user has already waited out the encode.
    if (blob.size > targetBytes) {
      const corrected = retryBitrate(bitrate, blob.size, targetBytes);

      if (corrected < bitrate) {
        const retried = await encodePass(corrected);
        if (retried.size > 0 && retried.size < blob.size) blob = retried;
      }
    }

    if (!blob.size || blob.size >= file.size) return null;
    if (!(await hasVideoTrack(blob))) return null;

    onProgress?.(1);
    return toFile(blob, file.name, mimeType);
  } finally {
    stream?.getTracks().forEach((track) => track.stop());
    if (video) {
      video.onended = null;
      video.onerror = null;
      video.ontimeupdate = null;
      video.onseeked = null;
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
    audioContext?.close().catch(() => {});
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}
